import type { VectorGeometryResult } from '../../types';

export interface DxfExportOptions {
  geometry: VectorGeometryResult;
  includeCut?: boolean;
  includeScore?: boolean;
  includeBacker?: boolean;
}

/**
 * Generates an AutoCAD R14 ASCII DXF file with standard layer assignments for laser & CNC machines.
 */
export function generateLaserDxf(options: DxfExportOptions): string {
  const { geometry, includeCut = true, includeScore = true, includeBacker = true } = options;

  let entities = '';

  // 1. Cut Layer (Layer: CUT, Color: 1 = Red)
  if (includeCut && geometry.cutPaths.length > 0) {
    for (const d of geometry.cutPaths) {
      entities += svgPathToDxfPolylines(d, 'CUT', 1);
    }
  }

  // 2. Score Layer (Layer: SCORE, Color: 5 = Blue)
  if (includeScore && geometry.scorePaths.length > 0) {
    for (const d of geometry.scorePaths) {
      entities += svgPathToDxfPolylines(d, 'SCORE', 5);
    }
  }

  // 3. Backer Layer (Layer: BACKER, Color: 3 = Green)
  if (includeBacker && geometry.backerPaths.length > 0) {
    for (const d of geometry.backerPaths) {
      entities += svgPathToDxfPolylines(d, 'BACKER', 3);
    }
  }

  return `0
SECTION
2
HEADER
9
$ACADVER
1
AC1014
9
$INSUNITS
70
4
0
ENDSEC
0
SECTION
2
TABLES
0
TABLE
2
LAYER
70
3
0
LAYER
2
CUT
70
0
62
1
6
CONTINUOUS
0
LAYER
2
SCORE
70
0
62
5
6
CONTINUOUS
0
LAYER
2
BACKER
70
0
62
3
6
CONTINUOUS
0
ENDTAB
0
ENDSEC
0
SECTION
2
ENTITIES
${entities}0
ENDSEC
0
EOF`;
}

/**
 * Parses SVG path 'd' commands into DXF LWPOLYLINE entity strings.
 */
function svgPathToDxfPolylines(d: string, layer: string, colorIndex: number): string {
  const points: { x: number; y: number }[] = [];
  let curX = 0;
  let curY = 0;
  let dxfOutput = '';

  const emitPolyline = (pts: { x: number; y: number }[], closed: boolean) => {
    if (pts.length < 2) return '';
    let out = `0\nLWPOLYLINE\n8\n${layer}\n62\n${colorIndex}\n90\n${pts.length}\n70\n${closed ? 1 : 0}\n`;
    for (const pt of pts) {
      // Invert Y coordinate for standard CAD Cartesian orientation
      out += `10\n${round(pt.x)}\n20\n${round(-pt.y)}\n`;
    }
    return out;
  };

  // Extract path commands
  const commands = d.match(/[a-df-z][^a-df-z]*/gi) || [];

  for (const cmdStr of commands) {
    const cmd = cmdStr[0];
    const args = cmdStr
      .slice(1)
      .trim()
      .split(/[\s,]+/)
      .map(Number)
      .filter((n) => !isNaN(n));

    if (cmd === 'M' || cmd === 'm') {
      if (points.length > 1) {
        dxfOutput += emitPolyline(points, false);
        points.length = 0;
      }
      curX = cmd === 'm' ? curX + args[0] : args[0];
      curY = cmd === 'm' ? curY + args[1] : args[1];
      points.push({ x: curX, y: curY });
    } else if (cmd === 'L' || cmd === 'l') {
      for (let i = 0; i < args.length; i += 2) {
        curX = cmd === 'l' ? curX + args[i] : args[i];
        curY = cmd === 'l' ? curY + args[i + 1] : args[i + 1];
        points.push({ x: curX, y: curY });
      }
    } else if (cmd === 'C' || cmd === 'c') {
      // Approximate cubic Bezier with polyline samples
      for (let i = 0; i < args.length; i += 6) {
        const x1 = cmd === 'c' ? curX + args[i] : args[i];
        const y1 = cmd === 'c' ? curY + args[i + 1] : args[i + 1];
        const x2 = cmd === 'c' ? curX + args[i + 2] : args[i + 2];
        const y2 = cmd === 'c' ? curY + args[i + 3] : args[i + 3];
        const x3 = cmd === 'c' ? curX + args[i + 4] : args[i + 4];
        const y3 = cmd === 'c' ? curY + args[i + 5] : args[i + 5];

        const samples = 8;
        for (let s = 1; s <= samples; s++) {
          const t = s / samples;
          const px =
            Math.pow(1 - t, 3) * curX +
            3 * Math.pow(1 - t, 2) * t * x1 +
            3 * (1 - t) * Math.pow(t, 2) * x2 +
            Math.pow(t, 3) * x3;
          const py =
            Math.pow(1 - t, 3) * curY +
            3 * Math.pow(1 - t, 2) * t * y1 +
            3 * (1 - t) * Math.pow(t, 2) * y2 +
            Math.pow(t, 3) * y3;
          points.push({ x: px, y: py });
        }
        curX = x3;
        curY = y3;
      }
    } else if (cmd === 'Q' || cmd === 'q') {
      // Approximate quadratic Bezier with polyline samples
      for (let i = 0; i < args.length; i += 4) {
        const x1 = cmd === 'q' ? curX + args[i] : args[i];
        const y1 = cmd === 'q' ? curY + args[i + 1] : args[i + 1];
        const x2 = cmd === 'q' ? curX + args[i + 2] : args[i + 2];
        const y2 = cmd === 'q' ? curY + args[i + 3] : args[i + 3];

        const samples = 6;
        for (let s = 1; s <= samples; s++) {
          const t = s / samples;
          const px = Math.pow(1 - t, 2) * curX + 2 * (1 - t) * t * x1 + Math.pow(t, 2) * x2;
          const py = Math.pow(1 - t, 2) * curY + 2 * (1 - t) * t * y1 + Math.pow(t, 2) * y2;
          points.push({ x: px, y: py });
        }
        curX = x2;
        curY = y2;
      }
    } else if (cmd === 'Z' || cmd === 'z') {
      if (points.length > 1) {
        dxfOutput += emitPolyline(points, true);
        points.length = 0;
      }
    }
  }

  if (points.length > 1) {
    dxfOutput += emitPolyline(points, false);
  }

  return dxfOutput;
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}
